'use client';

import { useVirtualizer } from '@tanstack/react-virtual';
import { type ComponentPropsWithoutRef, memo, useEffect, useRef } from 'react';
import { useResizeDetector } from 'react-resize-detector';

export type VirtualizedGridProps<Item> = ComponentPropsWithoutRef<'div'> & {
  /** Callback to fetch next page of items */
  fetchNextPage?: () => void;
  /** Gap between items in pixels */
  gap: number;
  /** Function to calculate item height based on item data and container width */
  getItemSize: (params: { gap: number; index: number; item: Item; lanes: number; width: number }) => number;
  /** Function to calculate loader item height */
  getItemLoaderSize?: (params: { gap: number; index: number; lanes: number; width: number }) => number;
  /** Whether there are more items to load */
  hasNextPage?: boolean;
  /** Whether currently fetching next page */
  isFetchingNextPage?: boolean;
  /** Array of items to render */
  items: Item[];
  /** Number of columns */
  lanes: number;
  /** Number of items to render outside visible area */
  overscan?: number;
  /** Render function for each item */
  renderItem: ({ index, item }: { index: number; item: Item }) => JSX.Element;
  /** Render function for loader items */
  renderItemLoader?: ({ index }: { index: number }) => JSX.Element;
  /** The scrollable container element */
  scrollElement: HTMLElement | null;
  /** Offset from top of scroll container to the grid */
  scrollMargin?: number;
};

const _VirtualizedGrid = <Item,>({
  fetchNextPage,
  gap = 16,
  getItemSize,
  getItemLoaderSize,
  hasNextPage,
  isFetchingNextPage,
  items,
  lanes = 2,
  overscan = 5,
  renderItem,
  renderItemLoader,
  scrollElement,
  scrollMargin = 0,
}: VirtualizedGridProps<Item>) => {
  const listRef = useRef<HTMLDivElement | null>(null);
  const { ref: resizeRef, width = 0 } = useResizeDetector();

  const rowVirtualizer = useVirtualizer({
    count: hasNextPage ? items.length + 1 : items.length,
    estimateSize: (index) => {
      const isLoaderRow = hasNextPage && index > items.length - lanes;

      if (isLoaderRow) return getItemLoaderSize?.({ gap, index, lanes, width }) ?? 0;

      const item = items[index];

      return getItemSize({ gap, index, item, lanes, width });
    },
    getScrollElement: () => scrollElement,
    lanes,
    overscan,
    scrollMargin,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  // Remeasure the list when the width changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: Need to remeasure when width changes
  useEffect(() => {
    rowVirtualizer.measure();
  }, [rowVirtualizer, width]);

  // Fetch next page when scrolling near the end
  useEffect(() => {
    if (!fetchNextPage) return;

    const [lastItem] = [...virtualItems].reverse();

    if (!lastItem) return;

    if (lastItem.index >= items.length - 1 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, items.length, virtualItems]);

  return (
    <div className="w-full" ref={listRef}>
      <div
        className="relative w-full"
        ref={resizeRef}
        style={{
          height: rowVirtualizer.getTotalSize(),
        }}
      >
        {virtualItems.map((virtualRow) => {
          const isLoaderRow = virtualRow.index > items.length - 1;
          const item = items[virtualRow.index];

          // Calculate left position for this lane
          const laneIndex = virtualRow.lane;
          const leftPercent = (laneIndex * 100) / lanes;
          const leftOffset = (laneIndex * gap) / lanes;
          const columnWidth = 100 / lanes;
          const widthOffset = (gap * (lanes - 1)) / lanes;

          return (
            <div
              className="absolute top-0"
              data-index={virtualRow.index}
              key={virtualRow.key}
              ref={virtualRow.measureElement}
              style={{
                height: isLoaderRow
                  ? (getItemLoaderSize?.({ gap, index: virtualRow.index, lanes, width }) ?? 0)
                  : getItemSize({ gap, index: virtualRow.index, item, lanes, width }),
                left: laneIndex === 0 ? 0 : `calc(${leftPercent}% + ${leftOffset}px)`,
                transform: `translateY(${virtualRow.start - rowVirtualizer.options.scrollMargin}px)`,
                width: `calc(${columnWidth}% - ${widthOffset}px)`,
              }}
            >
              {isLoaderRow
                ? renderItemLoader?.({ index: virtualRow.index })
                : renderItem({ index: virtualRow.index, item })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const VirtualizedGrid = memo(_VirtualizedGrid) as typeof _VirtualizedGrid;

// @ts-expect-error - displayName is valid on memoized components
VirtualizedGrid.displayName = 'VirtualizedGrid';
